import React from "react";
import { Link } from "react-router-dom";
import { featureManifest, BY_ROLE_PAGES } from "../featureManifest";
import { getMock } from "../components/FeatureMockups";
import FinalCTA from "../components/FinalCTA";
import LaunchSandboxButton from "../components/LaunchSandboxButton";

// ─────────────────────────────────────────────────────────────
// HERO
// ─────────────────────────────────────────────────────────────
function Hero() {
  const HeroMock = getMock("jobs-detail");
  return (
    <section className="mk-section !pt-20 md:!pt-32 !pb-20 relative overflow-hidden" data-testid="mk-hero">
      <div className="mk-grid-bg absolute inset-0 opacity-50 pointer-events-none" />
      {/* radial glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          inset: "-20%",
          background:
            "radial-gradient(ellipse at 20% 10%, rgba(19,93,212,0.18), transparent 50%), radial-gradient(ellipse at 85% 85%, rgba(214,239,55,0.12), transparent 55%)",
        }}
      />
      <div className="mk-container relative z-10">
        <div className="grid gap-12 lg:grid-cols-[1.15fr_1fr] items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 mb-6 mk-mono text-[10px] font-bold uppercase tracking-[0.3em] rounded-full px-3 py-1.5"
              style={{ border: "1px solid var(--mk-border-accent)", background: "rgba(214,239,55,0.06)", color: "var(--mk-accent)" }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--mk-accent)" }} />
              Multi-tenant · White-label · Production-grade
            </div>
            <h1 className="mk-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Maintenance,<br />
              <span style={{
                backgroundImage: "linear-gradient(90deg, var(--mk-accent), var(--mk-primary))",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}>
                properly engineered.
              </span>
            </h1>
            <p className="mt-6 text-lg text-[var(--mk-text-dim)] max-w-xl leading-relaxed">
              Fixlr is the operations platform for teams that run on minutes, not days — jobs, quotes, in-house operatives, supplier networks, invoices, and live geo-tracked service delivery, all in one multi-tenant engine.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <div className="mk-beam-border">
                <Link to="/signup" className="mk-btn mk-btn-primary text-base px-6 py-3" data-testid="mk-home-cta-primary">
                  Start free trial →
                </Link>
              </div>
              <LaunchSandboxButton
                label="Try live sandbox →"
                testId="mk-home-cta-sandbox"
                className="text-base px-6 py-3"
                variant="secondary"
              />
              <Link to="/product/demo" className="mk-btn mk-btn-ghost text-base px-6 py-3" data-testid="mk-home-cta-secondary">
                Book a 20-min demo
              </Link>
            </div>
            <div className="mt-6 mk-mono text-[11px] text-[var(--mk-text-muted)] uppercase tracking-[0.2em]">
              7-day free trial · No credit card · Sandbox pre-loaded with demo data
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 pointer-events-none" style={{
              background: "radial-gradient(ellipse at center, var(--mk-accent-glow), transparent 65%)",
              filter: "blur(40px)",
              opacity: 0.6,
            }} />
            <div className="relative mk-reveal"><HeroMock /></div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// LOGO banner
// ─────────────────────────────────────────────────────────────
function ClientLogos() {
  const logos = [
    "Belmont Estates", "Capital Facilities", "North Tower Group", "Greenline Holdings",
    "Arc Properties", "MetroCare FM", "Thames & Co.",
  ];
  return (
    <section className="border-y" style={{ borderColor: "var(--mk-border-subtle)", background: "rgba(20,20,20,0.55)" }} data-testid="mk-logo-banner">
      <div className="mk-container py-10">
        <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.35em] text-center text-[var(--mk-text-muted)] mb-6">
          Trusted by facilities teams running thousands of properties
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70">
          {logos.map((l) => (
            <div key={l} className="mk-display text-sm md:text-base font-extrabold tracking-tighter text-[var(--mk-text-dim)] whitespace-nowrap">
              {l}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// BENTO grid — all features grouped
// ─────────────────────────────────────────────────────────────
function BentoCard({ feature, size = "m" }) {
  const Mock = getMock(feature.sections?.[0]?.screenshotSlug || "");
  const sizes = {
    s: "col-span-12 md:col-span-6 lg:col-span-4",
    m: "col-span-12 md:col-span-6 lg:col-span-6",
    l: "col-span-12 lg:col-span-8",
    xl: "col-span-12",
  };
  return (
    <Link
      to={`/product/${feature.slug}`}
      className={`group rounded-2xl border p-6 md:p-7 relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-[var(--mk-border-strong)] block ${sizes[size]}`}
      style={{ borderColor: "var(--mk-border-subtle)", background: "var(--mk-surface)" }}
      data-testid={`mk-bento-${feature.slug}`}
    >
      <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-accent)] mb-3">
        {feature.category}
      </div>
      <h3 className="text-xl md:text-2xl font-extrabold tracking-tight mk-display mb-2 leading-tight">
        {feature.nav}
      </h3>
      <p className="text-sm text-[var(--mk-text-dim)] mb-5 leading-relaxed">
        {feature.subtitle.split(".")[0]}.
      </p>
      {(size === "l" || size === "xl") && (
        <div className="mt-3 relative opacity-90 group-hover:opacity-100 transition-opacity" style={{ transform: "scale(0.92)", transformOrigin: "top left" }}>
          <Mock />
        </div>
      )}
      <div className="mt-4 mk-mono text-[10px] text-[var(--mk-accent)] uppercase tracking-[0.25em] opacity-0 group-hover:opacity-100 transition-opacity">
        Explore →
      </div>
    </Link>
  );
}

function BentoGrid() {
  const core = featureManifest.filter((f) => f.category === "Core Ops");
  const portals = featureManifest.filter((f) => f.category === "Portals");
  const supplier = featureManifest.filter((f) => f.category === "Supplier");
  const mgmt = featureManifest.filter((f) => f.category === "Management");
  const ai = featureManifest.filter((f) => f.category === "Intelligence");

  return (
    <section className="mk-section" data-testid="mk-bento">
      <div className="mk-container">
        <div className="max-w-3xl mb-12">
          <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-4">
            Every inch of the platform
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mk-display">
            One engine for every moving part of maintenance ops.
          </h2>
          <p className="mt-4 text-base md:text-lg text-[var(--mk-text-dim)]">
            Jobs, quotes, invoices, operatives, suppliers, compliance, assets, AI, webhooks — if it touches maintenance, Fixlr has a dedicated, production-grade module for it.
          </p>
        </div>

        {/* Core ops — big feature card + medium cards */}
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          <BentoCard feature={core[0]} size="l" />   {/* Jobs — large with mock */}
          <BentoCard feature={core[1]} size="s" />   {/* Emergency */}
          <BentoCard feature={core[2]} size="s" />   {/* Quotes */}
          <BentoCard feature={core[3]} size="m" />   {/* Scheduling */}
          <BentoCard feature={portals[0]} size="m" />{/* In-House */}
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-8 mt-6 md:mt-8">
          <BentoCard feature={ai[0]} size="l" />     {/* AI Supplier Matching w/ mock */}
          <BentoCard feature={portals[1]} size="s" />{/* Navigation */}
          <BentoCard feature={portals[2]} size="s" />{/* Client portal */}
        </div>

        {/* New this quarter — Contracts, Client Portal, Staff Monitoring */}
        <div className="mt-14 mb-8" data-testid="mk-new-lane">
          <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-2">
            New this quarter
          </div>
          <h3 className="text-2xl md:text-3xl font-bold mk-display">Planned maintenance, a real client login, and a team you can actually see.</h3>
        </div>
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          <BentoCard feature={featureManifest.find((f) => f.slug === "contracts")} size="l" />
          <BentoCard feature={featureManifest.find((f) => f.slug === "client-portal")} size="s" />
          <BentoCard feature={featureManifest.find((f) => f.slug === "staff-monitoring")} size="s" />
        </div>

        {/* Supplier lane */}
        <div className="mt-14 mb-8">
          <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-2">
            The supplier lane
          </div>
          <h3 className="text-2xl md:text-3xl font-bold mk-display">Manage your whole external network from one screen.</h3>
        </div>
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {supplier.map((f, i) => (
            <BentoCard key={f.slug} feature={f} size={i === 0 ? "l" : "s"} />
          ))}
        </div>

        {/* Management lane */}
        <div className="mt-14 mb-8">
          <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-2">
            Money, assets, compliance
          </div>
          <h3 className="text-2xl md:text-3xl font-bold mk-display">The grown-up layer underneath the operations.</h3>
        </div>
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {mgmt.map((f, i) => (
            <BentoCard key={f.slug} feature={f} size={i === 0 ? "l" : i === 1 ? "s" : i === 2 ? "s" : "m"} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// AI feature spotlight — tracing-beam card
// ─────────────────────────────────────────────────────────────
function AISpotlight() {
  const Mock = getMock("ai-supplier-match");
  return (
    <section className="mk-section relative overflow-hidden" data-testid="mk-ai-spotlight">
      <div className="mk-grid-bg absolute inset-0 opacity-60 pointer-events-none" />
      <div className="mk-container relative z-10">
        <div className="grid gap-10 lg:gap-16 items-center lg:grid-cols-2">
          <div>
            <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-4">
              Intelligence · Premium differentiator
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mk-display mb-5 leading-[1.05]">
              Ask Fixlr:<br /> "Who's best for this job?"
            </h2>
            <p className="text-lg text-[var(--mk-text-dim)] max-w-xl leading-relaxed">
              AI Supplier Matching ranks your shortlist by distance, compliance, SLA hit-rate, and price history. Not a chatbot. A decision co-pilot.
            </p>
            <div className="mt-6 flex gap-3">
              <Link to="/product/ai-supplier-matching" className="mk-btn mk-btn-primary">Learn more →</Link>
              <Link to="/product/emergency" className="mk-btn mk-btn-secondary">Emergency response</Link>
            </div>
          </div>
          <div className="relative">
            <div className="mk-beam-border p-[2px] rounded-xl">
              <Mock />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// By role strip
// ─────────────────────────────────────────────────────────────
function ByRole() {
  return (
    <section className="mk-section" data-testid="mk-by-role">
      <div className="mk-container">
        <div className="max-w-3xl mb-10">
          <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-4">
            By role
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mk-display">
            Whatever hat you wear, Fixlr is already shaped for it.
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {BY_ROLE_PAGES.map((r) => (
            <Link
              key={r.slug}
              to={`/product/${r.slug}`}
              className="rounded-2xl overflow-hidden border relative group hover:-translate-y-1 transition-all duration-300 block"
              style={{ borderColor: "var(--mk-border-subtle)", background: "var(--mk-surface)" }}
              data-testid={`mk-role-card-${r.slug}`}
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img src={r.heroImage} alt="" className="w-full h-full object-cover" style={{ filter: "saturate(0.85) contrast(1.08) brightness(0.85)" }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,20,20,0.1), rgba(20,20,20,0.85))" }} />
              </div>
              <div className="p-5">
                <div className="text-sm font-bold">{r.nav}</div>
                <div className="text-xs text-[var(--mk-text-dim)] mt-1 line-clamp-2">{r.subheadline}</div>
                <div className="mt-4 mk-mono text-[10px] text-[var(--mk-accent)] uppercase tracking-[0.25em]">Explore →</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Social proof / quote
// ─────────────────────────────────────────────────────────────
function Testimonial() {
  return (
    <section className="mk-section" data-testid="mk-testimonial">
      <div className="mk-container">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-6">
            The verdict
          </div>
          <blockquote className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mk-display leading-tight">
            “We killed three tools the first week. Our SLA hit-rate went from 71% to 94% in two months.”
          </blockquote>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <div className="h-12 w-12 rounded-full grid place-items-center font-black text-white" style={{ background: "var(--mk-primary)" }}>C</div>
            <div className="text-left">
              <div className="text-sm font-semibold">Chris H.</div>
              <div className="text-xs text-[var(--mk-text-dim)]">Head of Operations, Belmont Estates</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Stats band
// ─────────────────────────────────────────────────────────────
function Stats() {
  const rows = [
    ["94%", "Avg SLA hit rate across live tenants"],
    ["06:30", "Briefing emails fire at each tenant's local time"],
    ["<2m", "From job creation to operative push"],
    ["487", "Suppliers bulk-invited in the biggest Fixlr onboarding"],
  ];
  return (
    <section className="border-y" style={{ borderColor: "var(--mk-border-subtle)", background: "rgba(20,20,20,0.55)" }} data-testid="mk-stats">
      <div className="mk-container grid md:grid-cols-4 gap-0">
        {rows.map(([v, l], i) => (
          <div
            key={l}
            className="py-10 px-6 text-center md:text-left"
            style={{ borderLeft: i === 0 ? "none" : "1px solid var(--mk-border-subtle)" }}
          >
            <div className="text-4xl md:text-5xl font-black mk-display" style={{ color: "var(--mk-accent)" }}>
              {v}
            </div>
            <div className="mt-2 mk-mono text-[10px] uppercase tracking-[0.25em] text-[var(--mk-text-muted)]">
              {l}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
export default function MarketingHome() {
  return (
    <div data-testid="mk-home">
      <Hero />
      <ClientLogos />
      <BentoGrid />
      <AISpotlight />
      <Stats />
      <ByRole />
      <Testimonial />
      <FinalCTA />
    </div>
  );
}
